import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { authClient, useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { sfx } from "@/lib/sounds";
import { ExternalLink, Loader2 } from "lucide-react";

/**
 * Store link that:
 *  - Signed-in: mints an SSO token and hands off to the WP store domain.
 *  - Signed-out: goes to internal /store page (or opens auth).
 *
 * Renders as either a desktop NavLink or a mobile row.
 */
export default function StoreLink({ mobile = false, onClickAny }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  const go = async (e) => {
    onClickAny?.();
    if (!user) {
      // Fall through to internal /store — the NavLink default target
      sfx.click();
      return;
    }
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    sfx.click();
    try {
      const { data } = await authClient.post("/sso/create-token", { return_to: "/lostdc-account/" });
      if (data?.redirect_url) {
        // Full-page redirect (WordPress domain).
        window.location.href = data.redirect_url;
        return;
      }
      // Store URL not configured — fall back to the internal /store page
      toast.info("Store link not configured — opening in-app store instead.");
      nav("/store");
    } catch (err) {
      const msg = err?.response?.data?.detail || "SSO failed";
      toast.error(typeof msg === "string" ? msg : "SSO failed");
      nav("/store");
    }
    setBusy(false);
  };

  if (mobile) {
    return (
      <button
        data-testid="mobile-nav-link-store"
        onClick={go}
        className="px-3 py-2 text-sm rounded-md text-white/80 hover:bg-white/5 flex items-center gap-1.5 text-left"
      >
        Store {user && <ExternalLink size={11} className="opacity-60" />}
      </button>
    );
  }

  return (
    <NavLink
      to="/store"
      data-testid="nav-link-store"
      onClick={go}
      onMouseEnter={() => sfx.hover()}
      className={({ isActive }) =>
        `relative px-3 py-2 text-sm uppercase tracking-wider font-medium rounded-md transition-colors inline-flex items-center gap-1 ${
          isActive ? "text-[#FFBF00]" : "text-white/70 hover:text-white"
        }`
      }
    >
      Store
      {busy
        ? <Loader2 size={11} className="animate-spin opacity-70" />
        : user && <ExternalLink size={10} className="opacity-60" />}
    </NavLink>
  );
}
